/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

export default function OcorrenciasFornecedoresTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [error, setError] = useState('');
  const [dataInicio, setDataInicio] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dataFim, setDataFim] = useState(format(new Date(), 'yyyy-MM-dd'));

  const API = '/api';

  const fetchOcorrencias = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', `${dataInicio}T00:00:00`);
      if (dataFim) params.append('data_fim', `${dataFim}T23:59:59`);
      params.append('limit', '100');

      const res = await fetch(`${API}/fornecedores/auditoria?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOcorrencias(data);
      } else if (res.status === 403) {
        setError('Acesso restrito a administradores');
      } else {
        setError('Erro ao carregar ocorrências');
      }
    } catch (e) {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [token, API, dataInicio, dataFim]);

  useEffect(() => {
    fetchOcorrencias();
  }, [fetchOcorrencias]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), "dd/MM/yy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getAcaoIcon = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'CREATE': return <AddIcon fontSize="small" />;
      case 'UPDATE': return <EditIcon fontSize="small" />;
      case 'DELETE': return <DeleteIcon fontSize="small" />;
      case 'UPLOAD': return <AddIcon fontSize="small" />;
      case 'OCORRENCIA_CREATE': return <AddIcon fontSize="small" />;
      case 'OCORRENCIA_UPDATE': return <EditIcon fontSize="small" />;
      case 'OCORRENCIA_DELETE': return <DeleteIcon fontSize="small" />;
      default: return <EditIcon fontSize="small" />;
    }
  };

  const getAcaoColor = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      case 'UPLOAD': return 'info';
      case 'OCORRENCIA_CREATE': return 'success';
      case 'OCORRENCIA_UPDATE': return 'warning';
      case 'OCORRENCIA_DELETE': return 'error';
      default: return 'info';
    }
  };

  const formatAcaoLabel = (acao) => {
    const labels = {
      'CREATE': 'Criação',
      'UPDATE': 'Edição',
      'DELETE': 'Exclusão',
      'UPLOAD': 'Upload',
      'OCORRENCIA_CREATE': 'Nova Ocorrência',
      'OCORRENCIA_UPDATE': 'Edição Ocorrência',
      'OCORRENCIA_DELETE': 'Exclusão Ocorrência',
    };
    return labels[acao?.toUpperCase()] || acao;
  };

  const formatCampo = (campo) => {
    const nomes = {
      'nome': 'Nome',
      'cnpj': 'CNPJ',
      'cpf': 'CPF',
      'telefone': 'Telefone',
      'contato': 'Contato',
      'email': 'E-mail',
      'cep': 'CEP',
      'logradouro': 'Logradouro',
      'numero_endereco': 'Número',
      'complemento': 'Complemento',
      'bairro': 'Bairro',
      'cidade': 'Cidade',
      'estado': 'Estado',
      'situacao_cadastral': 'Situação Cadastral',
      'situacao_cadastral_arquivo': 'Arquivo Sit. Cadastral',
      'ativo': 'Ativo',
      'observacao': 'Observação',
    };
    return nomes[campo] || campo;
  };

  const formatValor = (valor) => {
    if (!valor) return '-';
    try {
      const parsed = JSON.parse(valor);
      if (Array.isArray(parsed)) {
        return parsed.length > 0 ? `${parsed.length} item(s)` : '-';
      }
      return String(parsed);
    } catch {
      return valor.length > 50 ? valor.substring(0, 50) + '...' : valor;
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        📋 Ocorrências de Edição em Fornecedores
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Data Início"
          type="date"
          value={dataInicio}
          onChange={(e) => setDataInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Data Fim"
          type="date"
          value={dataFim}
          onChange={(e) => setDataFim(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" onClick={fetchOcorrencias} disabled={loading}>
          Buscar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Fornecedor</TableCell>
                <TableCell>Ação</TableCell>
                <TableCell>Campo</TableCell>
                <TableCell>Valor Anterior</TableCell>
                <TableCell>Valor Novo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ocorrencias.map((oc) => (
                <TableRow key={oc.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {formatDateTime(oc.data_hora)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>
                    {oc.usuario_nome}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`#${oc.fornecedor_id}`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getAcaoIcon(oc.acao)}
                      label={formatAcaoLabel(oc.acao)}
                      color={getAcaoColor(oc.acao)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {formatCampo(oc.campo_alterado)}
                  </TableCell>
                  <TableCell sx={{ color: 'error.main', fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatValor(oc.valor_anterior)}
                  </TableCell>
                  <TableCell sx={{ color: 'success.main', fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatValor(oc.valor_novo)}
                  </TableCell>
                </TableRow>
              ))}
              {ocorrencias.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhuma ocorrência encontrada no período
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Exibindo {ocorrencias.length} ocorrências
      </Typography>
    </Box>
  );
}
